define('presentation/settings/billing/plans/viewModels/removePlanActionModalViewModel',[
    'businessServices/converters/currencyFormatter',
    'common/collections/collectionSorter',
    'common/promises/promiseFactory',
    'common/storage/commonState',
    'constants/autoAttendantVoiceRouteEnumerations',
    'constants/systemObjectIconNameConstants',
    'constants/userGroupConstants',
    'presentation/settings/billing/plans/facades/removePlanFacade',
    'presentation/settings/billing/plans/validators/removePlanActionModalValidator'
], function() {
    return function (planPresentationObject, constraints, closeAccountCallback) {
        const self = this;

        const CurrencyFormatterConstructor = require('businessServices/converters/currencyFormatter');
        const _currencyFormatter = new CurrencyFormatterConstructor();

        const SorterConstructor = require('common/collections/collectionSorter');
        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _commonState = require('common/storage/commonState');
        const _i18n = require('i18next');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');
        const _systemObjectIconNames = require('constants/systemObjectIconNameConstants');
        const _userGroupConstants = require('constants/userGroupConstants');
        const _voiceRouteEnumerations = require('constants/autoAttendantVoiceRouteEnumerations');

        let _facade = null;
        let _validator = null;

        const PLEASE_SELECT = _voiceRouteEnumerations.pleaseSelect;

        const _formatIntegrityWarnings = (integrityWarnings) => {
            let formattedIntegrityWarnings = integrityWarnings.map((integrityWarning) => {
                switch (integrityWarning.icon) {
                    case 'phoneNumber':
                        return Object.assign({}, integrityWarning, {
                            entityType: _commonState.typeDisplay.hostedNumber,
                            iconName: _systemObjectIconNames.hostedNumber,
                        });
                    case 'autoAttendant':
                        return Object.assign({}, integrityWarning, {
                            entityType: _commonState.typeDisplay.autoAttendant,
                            iconName: _systemObjectIconNames.autoAttendant,
                        });
                    case 'schedule':
                        return Object.assign({}, integrityWarning, {
                            entityType: _commonState.typeDisplay.schedule,
                            iconName: _systemObjectIconNames.schedule,
                        });
                    case 'webChatWidget':
                        return Object.assign({}, integrityWarning, {
                            entityType: _commonState.typeDisplay.webChatWidget,
                            iconName: _systemObjectIconNames.webChatWidget,
                        });
                    case 'outboundCallingCampaign':
                        return Object.assign({}, integrityWarning, {
                            entityType: _commonState.typeDisplay.outboundCallingCampaign,
                            iconName: _systemObjectIconNames.outboundCallingCampaign,
                        });
                    default:
                        return null;
                }
            });

            const sorter = new SorterConstructor();
            sorter.multiSort(formattedIntegrityWarnings, ["icon", "entityName"]);

            return formattedIntegrityWarnings;
        };

        self.planPresentationObject = planPresentationObject;
        self.onlyNonTelephonyPackage = ko.observable(false);
        self.callsRoutedToScript = ko.observableArray([]);
        self.sendAllCallsToSubscriber = ko.observable(PLEASE_SELECT);
        self.showCallsRoutedToScriptWarning = ko.observable(false);
        self.showSendsCallsToQuestion = ko.observable(false);
        self.chatsRoutedToScript = ko.observableArray([]);
        self.showChatsRoutedToScriptWarning = ko.observable(false);
        self.campaignsRoutedToScript = ko.observableArray([]);
        self.showCampaignsRoutedToScriptWarning = ko.observable(false);
        self.canDisplayValidation = ko.observable(false);
        self.removePlanType = ko.observable('normal');
        self.placeholderMessage = _i18n.t('removePlanActionModal:pleaseSelect');

        self.showRemovePlanConfirmationMessage = ko.pureComputed(() => constraints.isOnlyRemainingPackage);
        self.showRemovePlanWarningMessage = ko.pureComputed(() => self.removePlanType() === 'constraints' && self.showSendsCallsToQuestion());

        self.telephonyPlanName = ko.pureComputed(() => {
            if (constraints.telephonyPackage != null) {
                return constraints.telephonyPackage.name;
            }
            return "";
        });

        self.telephonyPlanPrice = ko.pureComputed(() => {
            if (constraints.telephonyPackage != null) {
                return _currencyFormatter.formatUSDForDisplay(constraints.telephonyPackage.pricePerMonth);
            }
            return "";
        });

        self.navigateToCloseAccount = () => closeAccountCallback();

        self.availableSubscribers = ko.pureComputed(() => {
            const users = _commonState.users().reduce((accumulator, user) => {
                if (user.isActive() && user.id !== planPresentationObject.userId) {
                    accumulator.push({
                        id: user.id,
                        subscriberType: user.type,
                        displayName: user.name(),
                        avatar: user.avatar()
                    });
                }
                return accumulator;
            }, []);
            const userGroups = _commonState.userGroups().reduce((accumulator, userGroup) => {
                if (userGroup.isActive()) {
                    accumulator.push({
                        id: _commonState.resolveGroupIdToGuid(userGroup.id),
                        subscriberType: userGroup.type,
                        displayName: userGroup.name(),
                        avatar: userGroup.avatars()
                    });
                }
                return accumulator;
            }, []);
            const availableSubscribers = users.concat(userGroups);
            const sorter = new SorterConstructor();
            sorter.sort(availableSubscribers, "displayName", true);
            return availableSubscribers;
        });

        self.sendAllCallsToDefaultSubscriberSelection = ko.pureComputed(() => {
            const systemAdminsId = _commonState.resolveGroupIdToGuid(_userGroupConstants.systemAdminsGroupId);
            return self.availableSubscribers().find((subscriber) => {
                return subscriber.id === systemAdminsId;
            });
        });

        self.disabledClick = () => _validator.validate();

        self.submitAction = () => {
            return _promiseFactory.deferIndefinitely((deferredObject) => {
                if (self.showSendsCallsToQuestion() === false) {
                    self.sendAllCallsToSubscriber(self.sendAllCallsToDefaultSubscriberSelection());
                }
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        if (isValid === false) {
                            self.canDisplayValidation(true);
                            deferredObject.resolve({isValid: isValid});
                            return;
                        }

                        const sendAllCallsTo = ko.unwrap(self.sendAllCallsToSubscriber());
                        _router.navigate(_navigationConfiguration.routesById.removePlan.baseUrl + planPresentationObject.billingPackageId + '/' + sendAllCallsTo.subscriberType + '/' + sendAllCallsTo.id);
                        deferredObject.resolve({isValid: isValid});
                    });
            });
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/settings/billing/plans/facades/removePlanFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/settings/billing/plans/validators/removePlanActionModalValidator');
            _validator = new ValidatorConstructor();

            return _initialize();
        };

        const _initialize = () => {

            self.callsRoutedToScript(_formatIntegrityWarnings(constraints.callsRoutedToScript));
            self.showCallsRoutedToScriptWarning(self.callsRoutedToScript().length > 0);

            self.chatsRoutedToScript(_formatIntegrityWarnings(constraints.chatsRoutedToScript));
            self.showChatsRoutedToScriptWarning(self.chatsRoutedToScript().length > 0);

            self.campaignsRoutedToScript(_formatIntegrityWarnings(constraints.campaignsRoutedToScript));
            self.showCampaignsRoutedToScriptWarning(self.campaignsRoutedToScript().length > 0);

            if (self.showCallsRoutedToScriptWarning()) {
                self.showSendsCallsToQuestion(true);
                self.removePlanType('constraints');
            } else if (self.showChatsRoutedToScriptWarning() || self.showCampaignsRoutedToScriptWarning()) {
                self.showSendsCallsToQuestion(false);
                self.removePlanType('constraints');
            } else {
                self.showSendsCallsToQuestion(false);
                self.removePlanType('normal');
            }

            _validator.registerViewModel(self, _facade);

            return _promiseFactory.wait();
        };
    };
});
